import { Link } from "react-router-dom";
import { Download, HandHeart, ArrowUpRight } from "lucide-react";
import { IMAGES, SPONSORSHIP, SPONSORSHIP_GUIDE_URL } from "@/lib/content";
import { Reveal, Eyebrow, PrimaryButton, GhostButton } from "@/components/site/ui";
import { scrollToId } from "@/components/sections/utils";

export function Sponsorship({ sponsors = [] }) {
  const featured = sponsors.filter((s) => s.tier === "featured");
  return (
    <section id="sponsorship" className="py-20 md:py-32 bg-gradient-to-b from-ivory to-blush/10">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-10 md:gap-14 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-[1.5rem] md:rounded-[2rem] border border-champagne/30 shadow-[0_20px_50px_rgba(184,122,91,0.15)]">
              <img src={IMAGES.podiumMVP} alt="Leah Thames with her MVP award" className="w-full object-cover object-top aspect-[4/3]" />
            </div>
          </Reveal>
          <div>
            <Reveal><Eyebrow>Sponsorship Opportunities</Eyebrow></Reveal>
            <Reveal delay={0.05}><h2 className="mt-4 font-heading text-3xl sm:text-4xl md:text-5xl text-ink leading-tight">{SPONSORSHIP.heading}</h2></Reveal>
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-4 font-body text-sm md:text-base text-ink/75 leading-relaxed">
                {SPONSORSHIP.paragraphs.map((p, i) => (<p key={i}>{p}</p>))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 rounded-[1.5rem] md:rounded-[2rem] bg-ink text-ivory p-6 sm:p-8">
                <HandHeart className="text-blush" size={26} />
                <h3 className="mt-4 font-heading text-xl md:text-2xl">{SPONSORSHIP.ctaTitle}</h3>
                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <PrimaryButton href={SPONSORSHIP_GUIDE_URL} testid="sponsorship-guide-btn" className="w-full sm:w-auto"><Download size={16} /> Download Sponsorship Guide</PrimaryButton>
                  <GhostButton onClick={() => scrollToId("contact")} testid="sponsorship-contact-btn" className="w-full sm:w-auto !text-ivory !border-ivory/40 hover:!bg-ivory/10">Get In Touch</GhostButton>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {featured.length > 0 && (
          <Reveal delay={0.1}>
            <div className="mt-16 md:mt-24 text-center" data-testid="sponsor-wall">
              <Eyebrow>Proud Partners</Eyebrow>
              <h3 className="mt-3 font-heading text-2xl md:text-4xl text-ink">Thank You To My Sponsors</h3>
              <div className="mt-9 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
                {featured.map((s) => {
                  const inner = (
                    <div className="flex h-28 items-center justify-center rounded-2xl bg-white border border-champagne/30 p-6 transition-all hover:-translate-y-1 hover:shadow-[0_14px_34px_rgba(184,122,91,0.15)]">
                      {s.logo_url
                        ? <img src={s.logo_url} alt={s.name} className="max-h-16 max-w-full object-contain" />
                        : <span className="font-heading text-lg text-ink">{s.name}</span>}
                    </div>
                  );
                  return s.website
                    ? <a key={s.id} href={s.website} target="_blank" rel="noopener noreferrer" data-testid={`sponsor-${s.id}`} title={s.name}>{inner}</a>
                    : <div key={s.id} data-testid={`sponsor-${s.id}`} title={s.name}>{inner}</div>;
                })}
              </div>
              <div className="mt-8">
                <Link to="/sponsors" data-testid="view-all-sponsors-link" className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-wide text-bronze hover:text-ink transition-colors">
                  View All Partners <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
